import ArrowUp from '@assets/images/icons/arrow-up-light.svg?react'
import { getDragAndDropAttributes } from '@components/ArtBoard/DragAndDrop/getDragAndDropAttributes'
import useOnDragStart from '@components/ArtBoard/DragAndDrop/hooks/useOnDragStart'
import {
  DRAGGABLE_TYPE,
  DROPZONE_TYPE,
} from '@components/ArtBoard/DragAndDrop/types'
import type { NodeClasses } from '@components/ArtBoard/Node'
import { Children } from '@components/ArtBoard/Node/components/Children'
import { ChildrenCorner } from '@components/ArtBoard/Node/components/ChildrenCorner'
import { NodeTags } from '@components/ArtBoard/Node/components/NodeTags'
import { ParentCorners } from '@components/ArtBoard/Node/components/ParentCorners'
import { VARIANTS } from '@components/ArtBoard/Node/constants'
import useActions from '@components/ArtBoard/Node/hooks/useActions'
import { NodePreviewWrapper } from '@components/ArtBoard/NodePreviewWrapper'
import { Tooltip } from '@lib/components'
import type { Notes } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { SeoActions } from '@store/reducers/seo/actions'
import type React from 'react'
import { useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { TREE_MATRIX } from '@/constants/constants'
import type { TreeItemVariants } from '@/types'
import { clsJoin } from '@/utils'
import { GaEar, Name, SeoBlockContainer } from './components'
import css from './styles.module.css'

interface Props {
  id: string
  title: string
  variant: TreeItemVariants
  isChild: boolean
  parentId: string | null
  hasChildren: boolean
  hasChild: boolean
  isCollapsed: boolean
  // todo: fix core project state, notes is required field but it missing in some projects (e.g. 3fkczdiq1tn)
  notes?: Notes
  url?: string | null
  nodeClasses: NodeClasses
  nodeStyles: React.CSSProperties
  color: string
  canDrag: boolean
  isOverlay?: boolean
  creationDisabled: boolean
}

export const NodeSeo: React.FC<Props> = ({
  id,
  title,
  variant,
  isChild,
  parentId,
  hasChildren,
  hasChild,
  isCollapsed,
  notes = {},
  url = null,
  nodeClasses,
  nodeStyles,
  color,
  canDrag,
  isOverlay = false,
  creationDisabled,
}) => {
  const dispatch = useDispatch()
  const ref = useRef<HTMLDivElement>(null)
  const onDragStart = useOnDragStart(ref, DRAGGABLE_TYPE.PAGE, id, {}, true)
  const dragAttributes =
    canDrag && !isOverlay ? { onPointerDown: onDragStart } : {}
  const dropAttributes = getDragAndDropAttributes(
    id,
    DROPZONE_TYPE.PAGE,
    { nodeId: id },
    isOverlay
  )

  const { onCollapse } = useActions(id)

  const treeMode = useSelector(migrationProjectSelector.getProjectTreeType)
  const isProjectNeutral = useSelector(
    migrationProjectSelector.getIsProjectNeutral
  )
  const projectTreeMode = useSelector(
    migrationProjectSelector.getProjectTreeType
  )

  const handleShowPanel = () => {
    if (!isOverlay) {
      dispatch(SeoActions.show(id))
    }
  }

  if (variant === VARIANTS.GHOST) {
    return (
      <div
        className={nodeClasses.container}
        style={nodeStyles}
        ref={ref}
        {...dragAttributes}
        {...dropAttributes}
      >
        {isChild && treeMode !== TREE_MATRIX && !isOverlay && (
          <ChildrenCorner id={id} parentId={parentId} />
        )}

        {(hasChildren || hasChild) &&
          treeMode !== TREE_MATRIX &&
          !isOverlay &&
          !isCollapsed && <ParentCorners />}

        <div
          className={clsJoin(
            css.node_seo_ghost,
            projectTreeMode === TREE_MATRIX && css.node_seo_matrix
          )}
          style={{ borderColor: color }}
        >
          <Name
            variant={variant}
            name={title}
            color={color}
            customClassName={css.node_seo_name}
          />
        </div>
      </div>
    )
  }

  return (
    <div
      className={nodeClasses.container}
      style={nodeStyles}
      ref={ref}
      {...dropAttributes}
      onClick={handleShowPanel}
    >
      {isChild && treeMode !== TREE_MATRIX && !isOverlay && (
        <ChildrenCorner id={id} parentId={parentId} />
      )}

      {(hasChildren || hasChild) &&
        treeMode !== TREE_MATRIX &&
        !isOverlay &&
        !isCollapsed && <ParentCorners />}

      {variant === VARIANTS.STACK && <div className={nodeClasses.stackGhost} />}

      <div className={clsJoin(nodeClasses.node, css.node_seo)}>
        <GaEar nodeId={id} url={url} />

        <div
          {...dragAttributes}
          className={css.drag_handle}
          onDragStart={(event) => event.preventDefault()}
        >
          <Name
            variant={variant}
            name={title}
            color={color}
            isProjectNeutral={isProjectNeutral}
          />

          <div className={nodeClasses.body}>
            <SeoBlockContainer notes={notes} />
          </div>
        </div>

        {!creationDisabled && !isCollapsed && !isOverlay && (
          <span
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <Children nodeId={id} />
          </span>
        )}

        {(hasChildren || hasChild) && !isCollapsed && !isOverlay && (
          // Обёртка — span: .node_seo:hover глушит position у div-детей ноды,
          // а контейнер Tooltip это div — иначе на ховере кнопка встаёт в
          // поток и дёргает карточку с дочерними страницами.
          <span className={css.collapse}>
            <Tooltip
              content="Collapse"
              position="top"
              onClick={(event) => {
                // Клик по контейнеру ноды открывает SEO-панель —
                // сворачивание не должно её тянуть за собой.
                event?.stopPropagation()
                onCollapse()
              }}
              className={css.collapse_button}
            >
              <ArrowUp />
            </Tooltip>
          </span>
        )}

        {isCollapsed && !isOverlay && <NodePreviewWrapper nodeId={id} />}

        {!isOverlay && (
          <div>
            <NodeTags id={id} />
          </div>
        )}
      </div>
    </div>
  )
}
